import { useEffect, useRef, useState } from "react";
import { useLang } from "@/context/LanguageContext";
import { useSettings } from "@/context/SettingsContext";
import { updateSettings, uploadImage, getErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Upload } from "lucide-react";
import QRPosters from "@/components/QRPosters";

// NOTE: Section and Field are defined at module scope (NOT inside the
// SettingsManager component). Defining them inside would give them a new
// function identity on every keystroke, causing React to unmount/remount every
// input — which loses focus and scrolls the page to the top on each keypress.
function Section({ title, children }) {
  return (
    <div className="rounded-2xl bg-white border border-black/5 p-6">
      <p className="text-xs uppercase tracking-[0.3em] text-[#0F5F63] mb-5">{title}</p>
      <div className="grid gap-4 md:grid-cols-2">{children}</div>
    </div>
  );
}

function Field({ label, k, type = "text", full, form, set }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label htmlFor={k}>{label}</Label>
      {type === "textarea" ? (
        <Textarea id={k} value={form[k] || ""} onChange={(e) => set(k, e.target.value)}
          data-testid={`settings-${k}`} rows={2} />
      ) : (
        <Input id={k} type={type} value={form[k] || ""} onChange={(e) => set(k, e.target.value)}
          data-testid={`settings-${k}`} />
      )}
    </div>
  );
}

export default function SettingsManager() {
  const { lang, t } = useLang();
  const { settings, reload } = useSettings();
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { setForm(settings); }, [settings]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadImage(file);
      set("logo_url", res.url);
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(getErrorMessage(err, "Upload failed"));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await updateSettings(form);
      await reload();
      toast.success(t.settingsSaved);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="settings-panel" className="space-y-6">
      <Section title={t.brandIdentity}>
        <Field label={t.brandNameEn} k="brand_name_en" form={form} set={set} />
        <Field label={t.brandNameAr} k="brand_name_ar" form={form} set={set} />
        <Field label={t.taglineEn} k="tagline_en" form={form} set={set} />
        <Field label={t.taglineAr} k="tagline_ar" form={form} set={set} />
        <Field label={t.descriptionEn} k="description_en" type="textarea" full form={form} set={set} />
        <Field label={t.descriptionAr} k="description_ar" type="textarea" full form={form} set={set} />
        <div className="md:col-span-2">
          <Label>{t.logo}</Label>
          <div className="flex gap-3 mt-1 items-center">
            <Input value={form.logo_url || ""} onChange={(e) => set("logo_url", e.target.value)}
              placeholder="https://…" data-testid="settings-logo_url" />
            <input type="file" ref={fileRef} accept="image/*" hidden onChange={handleLogoUpload} data-testid="settings-logo-file" />
            <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading}
              data-testid="settings-logo-upload"
              className="rounded-full border-[#0F5F63]/20 text-[#0F5F63] whitespace-nowrap">
              <Upload className="w-4 h-4 me-2" />
              {uploading ? t.uploading : t.uploadImage}
            </Button>
          </div>
          <p className="text-[10px] text-[#5C6B6D] mt-1">{t.logoHint}</p>
          {form.logo_url && (
            <img src={form.logo_url} alt="logo preview" className="mt-3 w-16 h-16 rounded-full object-cover border border-[#0F5F63]/10" />
          )}
        </div>
      </Section>

      <Section title={t.contact}>
        <Field label={t.contactEmail} k="contact_email" form={form} set={set} />
        <Field label={t.contactPhone} k="contact_phone" form={form} set={set} />
        <Field label={t.addressEn} k="address_en" form={form} set={set} />
        <Field label={t.addressAr} k="address_ar" form={form} set={set} />
      </Section>

      <Section title={t.social}>
        <Field label="Instagram" k="social_instagram" form={form} set={set} />
        <Field label="Twitter / X" k="social_twitter" form={form} set={set} />
        <Field label="Facebook" k="social_facebook" form={form} set={set} />
        <Field label="TikTok" k="social_tiktok" form={form} set={set} />
      </Section>

      <Section title={t.footer}>
        <Field label={t.footerNoteEn} k="footer_note_en" full form={form} set={set} />
        <Field label={t.footerNoteAr} k="footer_note_ar" full form={form} set={set} />
      </Section>

      <QRPosters />

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="settings-save-btn"
          className="rounded-full bg-[#0F5F63] hover:bg-[#0A4548] text-[#FDFBF7] uppercase tracking-widest text-xs h-11 px-8">
          {t.save}
        </Button>
      </div>
    </div>
  );
}
